import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, ShieldAlert, Wallet, ArrowUpRight, CheckCircle2, HelpCircle, Lock } from 'lucide-react';
import { usePlant } from '../../state/PlantContext';
import { useAuth } from '../../state/AuthContext';
import { permitApi, billingApi, conflictApi } from '../../lib/api';
import type { BalanceResponse, PermitListItem, SubscriptionResponse } from '../../lib/types';
import { ResultBadge, PermitStatusBadge } from '../../components/StatusBadge';
import { Spinner, EmptyState } from '../../components/Misc';
import { formatDateTime, formatEur, titleCase } from '../../lib/format';

export default function DashboardPage() {
  const { currentPlant, loading: plantLoading } = usePlant();
  const { user, org } = useAuth();
  const [permits, setPermits] = useState<PermitListItem[] | null>(null);
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [overrideCount, setOverrideCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [bal, sub] = await Promise.all([billingApi.balance(), billingApi.subscription()]);
        if (!cancelled) {
          setBalance(bal);
          setSubscription(sub);
        }
        if (currentPlant) {
          const [permitsRes, overridesRes] = await Promise.all([permitApi.list(currentPlant.id), conflictApi.listOverrides(currentPlant.id)]);
          if (!cancelled) {
            setPermits(permitsRes.permits);
            setOverrideCount(overridesRes.overrides.filter((o) => o.status === 'PENDING_CO_AUTHORIZATION').length);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [currentPlant?.id]);

  if (plantLoading || loading) return <Spinner label="Loading your dashboard…" />;

  if (!currentPlant) {
    return (
      <EmptyState
        icon={<ClipboardList size={36} />}
        title="No plant yet"
        description="Your organization doesn't have a plant configured. An org admin can add one to get started."
      />
    );
  }

  const activeStatuses = ['ISSUED', 'ACTIVE_FIREWATCH', 'EXTENDED'];
  const active = permits?.filter((p) => activeStatuses.includes(p.status)) ?? [];
  const criticalOpen = permits?.filter((p) => p.latestOverallResult === 'CRITICAL_STOP_WORK') ?? [];
  const clearCount = permits?.filter((p) => p.latestOverallResult === 'CLEAR').length ?? 0;

  return (
    <div className="stack gap-6">
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div className="stack gap-1">
          <span className="eyebrow">{currentPlant.code} · {currentPlant.name}</span>
          <h1 className="h2-section">Welcome back, {user?.name?.split(' ').slice(-1)[0] ?? 'there'}</h1>
          <p className="body-text">{org?.name} — {titleCase(user?.role ?? '')}</p>
        </div>
        <div className="spacer" />
        <Link to="/app/permits/new" className="btn btn-primary">New Permit</Link>
      </div>

      <div className="grid-4">
        <StatCard icon={<ClipboardList size={18} color="var(--accent)" />} label="Active permits" value={String(active.length)} sub={`${permits?.length ?? 0} total`} />
        <StatCard icon={<ShieldAlert size={18} color="var(--status-critical)" />} label="Critical stop-work" value={String(criticalOpen.length)} sub="open evaluations" tone={criticalOpen.length > 0 ? 'critical' : undefined} />
        <StatCard icon={<CheckCircle2 size={18} color="var(--status-clear)" />} label="Clear evaluations" value={String(clearCount)} sub="ready to issue" />
        <StatCard icon={<Lock size={18} color="var(--status-indeterminate)" />} label="Pending overrides" value={String(overrideCount ?? 0)} sub="need co-authorization" />
      </div>

      <div className="grid-2" style={{ alignItems: 'flex-start' }}>
        <div className="card stack gap-4">
          <div className="row">
            <div className="h3">Recent permits</div>
            <div className="spacer" />
            <Link to="/app/permits" className="link body-small row gap-1">View all <ArrowUpRight size={14} /></Link>
          </div>
          {permits && permits.length > 0 ? (
            <div className="stack gap-2">
              {permits.slice(0, 6).map((p) => (
                <Link key={p.id} to={`/app/permits/${p.id}`} className="row" style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <div className="stack gap-1" style={{ minWidth: 0 }}>
                    <div className="row gap-2">
                      <span className="mono" style={{ fontWeight: 600, fontSize: '0.875rem' }}>{p.permitNumber}</span>
                      <PermitStatusBadge status={p.status} />
                    </div>
                    <span className="body-small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.scopeText}</span>
                  </div>
                  <div className="spacer" />
                  <ResultBadge result={p.latestOverallResult} />
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState icon={<ClipboardList size={28} />} title="No permits yet" description="Create your first permit to run the conflict engine." action={<Link to="/app/permits/new" className="btn btn-primary btn-sm">New Permit</Link>} />
          )}
        </div>

        <div className="stack gap-4">
          <div className="card stack gap-3">
            <div className="row gap-2">
              <Wallet size={18} color="var(--accent)" />
              <div className="h3" style={{ fontSize: '1.0625rem' }}>Sensifai Marketplace</div>
            </div>
            {subscription?.linked ? (
              <>
                <BillingRow label="Plan tier" value={titleCase(org?.planTier ?? 'trial')} />
                <BillingRow label="Subscription status" value={subscription.subscription?.status ?? '—'} />
                <BillingRow label="Wallet balance" value={formatEur(balance?.balanceEur ?? null)} />
                {!balance?.live && <span className="body-small text-tertiary">Showing last cached value{balance?.cachedAt ? ` (${formatDateTime(balance.cachedAt)})` : ''} — Gatekeeper unreachable or not configured.</span>}
              </>
            ) : (
              <>
                <p className="body-small">This organization isn't linked to a Sensifai Marketplace billing account yet.</p>
                {subscription?.marketplaceUrl && (
                  <a href={subscription.marketplaceUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                    Link on Sensifai Marketplace
                  </a>
                )}
              </>
            )}
            <div className="divider" />
            <BillingRow label="Plants" value={`${1} / ${org?.maxPlants ?? '—'}`} />
            <BillingRow label="Concurrent active permits" value={`${active.length} / ${org?.maxActivePermits ?? '—'}`} />
          </div>

          <div className="card stack gap-2">
            <div className="row gap-2">
              <HelpCircle size={18} color="var(--accent)" />
              <div className="h3" style={{ fontSize: '1.0625rem' }}>Fail-closed, by design</div>
            </div>
            <p className="body-small">
              Every permit evaluation runs the deterministic conflict engine first. If it ever cannot
              produce a determination, the permit is blocked with <code className="mono">SYSTEM_INDETERMINATE_BLOCK</code> —
              never silently approved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sub, tone }: { icon: ReactNode; label: string; value: string; sub: string; tone?: 'critical' }) {
  return (
    <div className="card stack gap-2" style={tone === 'critical' && value !== '0' ? { borderColor: 'rgba(179,38,30,0.35)', background: 'var(--status-critical-bg)' } : undefined}>
      <div className="row gap-2">{icon}<span className="body-small" style={{ fontWeight: 600 }}>{label}</span></div>
      <div style={{ fontSize: '1.875rem', fontWeight: 800 }}>{value}</div>
      <span className="body-small text-tertiary">{sub}</span>
    </div>
  );
}

function BillingRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="row">
      <span className="body-small">{label}</span>
      <div className="spacer" />
      <span className="body-small mono" style={{ fontWeight: 600, color: 'var(--text)' }}>{value}</span>
    </div>
  );
}

